<template>
  <div class="company-list-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-building"></i></span> Firmenverwaltung
        </h1>
        <button class="btn btn-primary btn-with-icon with-hover-effect animate-fade-in" @click="showCreateForm">
          <span class="icon me-2"><i class="fas fa-plus-circle"></i></span> Neue Firma erstellen
        </button>
      </div>

      <div class="row">
        <div class="col-12">
          <Card animateIn="fade-in-up" class="card-with-form">
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 class="mb-0 mb-md-2">Alle Firmen</h4>
                <div class="search-container">
                  <div class="input-group with-focus-effect">
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                    <input 
                      type="text" 
                      class="form-control with-focus-effect" 
                      placeholder="Suche nach Firmen..." 
                      v-model="searchQuery"
                    >
                  </div>
                </div>
              </div>
            </template>
            
            <div v-if="loading" class="text-center py-5 content-loading">
              <div class="loader loader-lg mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Wird geladen...</span>
                </div>
              </div>
              <p class="text-muted">Firmen werden geladen...</p>
            </div>
            
            <div v-else-if="filteredCompanies.length === 0" class="text-center py-4 empty-state">
              <span class="icon icon-muted icon-xl mb-3"><i class="fas fa-building"></i></span>
              <p class="mb-0 fw-medium">Keine Firmen gefunden.</p>
              <p v-if="searchQuery" class="text-muted">
                Versuchen Sie andere Suchbegriffe oder 
                <a href="#" class="link-with-hover" @click.prevent="searchQuery = ''">setzen Sie die Suche zurück</a>.
              </p>
              <p v-else>
                <button class="btn btn-outline-primary btn-with-icon with-hover-effect mt-2" @click="showCreateForm">
                  <span class="icon me-1"><i class="fas fa-plus-circle"></i></span> Erste Firma erstellen
                </button>
              </p>
            </div>
            
            <div v-else>
              <div class="table-responsive">
                <table class="table table-hover data-table mb-0">
                  <thead>
                    <tr>
                      <th class="col-logo">Logo</th>
                      <th>Name</th>
                      <th>E-Mail</th>
                      <th>Telefon</th>
                      <th>Status</th>
                      <th class="col-actions text-end">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="company in paginatedCompanies" 
                      :key="company.id"
                      class="data-row with-transition"
                    >
                      <td>
                        <div class="company-logo-wrapper">
                          <img v-if="company.logo" :src="company.logo" :alt="`${company.name} Logo`" class="img-fluid" />
                          <div v-else class="company-logo-placeholder" :style="{ backgroundColor: getColorForCompany(company.id) }">
                            {{ getInitials(company.name) }}
                          </div>
                        </div>
                      </td>
                      <td class="fw-medium">{{ company.name }}</td>
                      <td>
                        <span v-if="company.email" class="data-info with-icon">
                          <span class="icon icon-muted me-1"><i class="fas fa-envelope"></i></span>
                          {{ company.email }}
                        </span>
                        <span v-else class="text-muted fst-italic">Keine E-Mail</span>
                      </td>
                      <td>
                        <span v-if="company.phone" class="data-info with-icon">
                          <span class="icon icon-muted me-1"><i class="fas fa-phone"></i></span>
                          {{ company.phone }}
                        </span>
                        <span v-else class="text-muted fst-italic">Keine Telefonnummer</span>
                      </td>
                      <td>
                        <span 
                          class="badge"
                          :class="company.active ? 'bg-success' : 'bg-secondary'"
                        >
                          {{ company.active ? 'Aktiv' : 'Inaktiv' }}
                        </span>
                      </td>
                      <td class="text-end">
                        <div class="btn-group">
                          <button 
                            class="btn btn-sm btn-icon btn-outline-primary with-hover-effect" 
                            title="Bearbeiten"
                            @click="editCompany(company)"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button 
                            class="btn btn-sm btn-icon btn-outline-danger with-hover-effect" 
                            title="Löschen"
                            @click="confirmDeleteCompany(company)"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div class="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                <div class="pagination-info text-muted">
                  Zeige {{ (currentPage - 1) * itemsPerPage + 1 }} bis 
                  {{ Math.min(currentPage * itemsPerPage, filteredCompanies.length) }} 
                  von {{ filteredCompanies.length }} Einträgen
                </div>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="filteredCompanies.length"
                  :per-page="itemsPerPage"
                  :limit="5"
                  align="center"
                  class="pagination-enhanced mb-0"
                ></b-pagination>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
    
    <!-- Modal: Neue Firma erstellen -->
    <b-modal 
      id="modal-create-company" 
      title="Neue Firma erstellen" 
      @ok="createCompany"
      @hidden="resetForm"
      :ok-disabled="!isFormValid || creatingCompany"
      ok-title="Erstellen"
      cancel-title="Abbrechen"
      centered
      class="modal-with-form"
    >
      <div v-if="creatingCompany" class="text-center py-4">
        <div class="loader loader-md mb-3">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Wird erstellt...</span>
          </div>
        </div>
        <p class="text-muted">Firma wird erstellt...</p>
      </div>
      
      <form v-else class="form-enhanced">
        <div class="mb-3">
          <label for="companyName" class="form-label required">Firmenname</label>
          <input 
            type="text" 
            class="form-control with-focus-effect" 
            id="companyName" 
            v-model="companyForm.name"
            :class="{ 'is-invalid': formErrors.name }"
            required
          >
          <div class="invalid-feedback" v-if="formErrors.name">
            {{ formErrors.name }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="companyEmail" class="form-label">E-Mail</label>
          <input 
            type="email" 
            class="form-control with-focus-effect" 
            id="companyEmail" 
            v-model="companyForm.email"
            :class="{ 'is-invalid': formErrors.email }"
          >
          <div class="invalid-feedback" v-if="formErrors.email">
            {{ formErrors.email }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="companyPhone" class="form-label">Telefon</label>
          <input 
            type="tel" 
            class="form-control with-focus-effect" 
            id="companyPhone" 
            v-model="companyForm.phone"
            :class="{ 'is-invalid': formErrors.phone }"
          >
          <div class="invalid-feedback" v-if="formErrors.phone">
            {{ formErrors.phone }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="companyWebsite" class="form-label">Website</label>
          <input 
            type="url" 
            class="form-control with-focus-effect" 
            id="companyWebsite" 
            v-model="companyForm.website"
            :class="{ 'is-invalid': formErrors.website }"
          >
          <div class="invalid-feedback" v-if="formErrors.website">
            {{ formErrors.website }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="companyLogo" class="form-label">Logo (URL)</label>
          <input 
            type="url" 
            class="form-control with-focus-effect" 
            id="companyLogo" 
            v-model="companyForm.logo"
            :class="{ 'is-invalid': formErrors.logo }"
          >
          <div class="invalid-feedback" v-if="formErrors.logo">
            {{ formErrors.logo }}
          </div>
          
          <div v-if="companyForm.logo" class="mt-3 logo-preview-container">
            <p class="form-label">Vorschau:</p>
            <div class="logo-preview-wrapper">
              <img :src="companyForm.logo" alt="Logo Vorschau" class="img-fluid" />
            </div>
          </div>
        </div>
        
        <div class="form-check form-switch form-check-enhanced mb-3">
          <input 
            class="form-check-input" 
            type="checkbox" 
            id="companyActive" 
            v-model="companyForm.active"
          >
          <label class="form-check-label" for="companyActive">
            Firma aktiv
          </label>
        </div>
        
        <div class="mb-3">
          <label for="companyDescription" class="form-label">Beschreibung</label>
          <textarea 
            class="form-control with-focus-effect" 
            id="companyDescription" 
            rows="3"
            v-model="companyForm.description"
            :class="{ 'is-invalid': formErrors.description }"
          ></textarea>
          <div class="invalid-feedback" v-if="formErrors.description">
            {{ formErrors.description }}
          </div>
        </div>
      </form>
    </b-modal>
    
    <!-- Modal: Firma bearbeiten -->
    <b-modal 
      id="modal-edit-company" 
      title="Firma bearbeiten" 
      @ok="updateCompany"
      :ok-disabled="!isFormValid || updatingCompany"
      ok-title="Speichern"
      cancel-title="Abbrechen"
      centered
      class="modal-with-form"
    >
      <div v-if="updatingCompany" class="text-center py-4">
        <div class="loader loader-md mb-3">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Wird aktualisiert...</span>
          </div>
        </div>
        <p class="text-muted">Firma wird aktualisiert...</p>
      </div>
      
      <form v-else class="form-enhanced">
        <div class="mb-3">
          <label for="editCompanyName" class="form-label required">Firmenname</label>
          <input 
            type="text" 
            class="form-control with-focus-effect" 
            id="editCompanyName" 
            v-model="companyForm.name"
            :class="{ 'is-invalid': formErrors.name }"
            required
          >
          <div class="invalid-feedback" v-if="formErrors.name">
            {{ formErrors.name }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="editCompanyEmail" class="form-label">E-Mail</label>
          <input 
            type="email" 
            class="form-control with-focus-effect" 
            id="editCompanyEmail" 
            v-model="companyForm.email"
            :class="{ 'is-invalid': formErrors.email }"
          >
          <div class="invalid-feedback" v-if="formErrors.email">
            {{ formErrors.email }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="editCompanyPhone" class="form-label">Telefon</label>
          <input 
            type="tel" 
            class="form-control with-focus-effect" 
            id="editCompanyPhone" 
            v-model="companyForm.phone"
            :class="{ 'is-invalid': formErrors.phone }"
          >
          <div class="invalid-feedback" v-if="formErrors.phone">
            {{ formErrors.phone }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="editCompanyWebsite" class="form-label">Website</label>
          <input 
            type="url" 
            class="form-control with-focus-effect" 
            id="editCompanyWebsite" 
            v-model="companyForm.website"
            :class="{ 'is-invalid': formErrors.website }"
          >
          <div class="invalid-feedback" v-if="formErrors.website">
            {{ formErrors.website }}
          </div>
        </div>
        
        <div class="mb-3">
          <label for="editCompanyLogo" class="form-label">Logo (URL)</label>
          <input 
            type="url" 
            class="form-control with-focus-effect" 
            id="editCompanyLogo" 
            v-model="companyForm.logo"
            :class="{ 'is-invalid': formErrors.logo }"
          >
          <div class="invalid-feedback" v-if="formErrors.logo">
            {{ formErrors.logo }}
          </div>
          
          <div v-if="companyForm.logo" class="mt-3 logo-preview-container">
            <p class="form-label">Vorschau:</p>
            <div class="logo-preview-wrapper">
              <img :src="companyForm.logo" alt="Logo Vorschau" class="img-fluid" />
            </div>
          </div>
        </div>
        
        <div class="form-check form-switch form-check-enhanced mb-3">
          <input 
            class="form-check-input" 
            type="checkbox" 
            id="editCompanyActive" 
            v-model="companyForm.active"
          >
          <label class="form-check-label" for="editCompanyActive">
            Firma aktiv
          </label>
        </div>
        
        <div class="mb-3">
          <label for="editCompanyDescription" class="form-label">Beschreibung</label>
          <textarea 
            class="form-control with-focus-effect" 
            id="editCompanyDescription" 
            rows="3"
            v-model="companyForm.description"
            :class="{ 'is-invalid': formErrors.description }"
          ></textarea>
          <div class="invalid-feedback" v-if="formErrors.description">
            {{ formErrors.description }}
          </div>
        </div>
      </form>
    </b-modal>
    
    <!-- Modal: Firma löschen -->
    <b-modal 
      id="modal-delete-company" 
      title="Firma löschen" 
      @ok="deleteCompany"
      :ok-disabled="deletingCompany"
      ok-title="Löschen"
      ok-variant="danger"
      cancel-title="Abbrechen"
      centered
      class="modal-with-confirm"
    >
      <div v-if="deletingCompany" class="text-center py-4">
        <div class="loader loader-md mb-3">
          <div class="spinner-border text-danger" role="status">
            <span class="visually-hidden">Wird gelöscht...</span>
          </div>
        </div>
        <p class="text-muted">Firma wird gelöscht...</p>
      </div>
      
      <template v-else>
        <div class="alert alert-warning alert-with-icon mb-4">
          <span class="alert-icon"><i class="fas fa-exclamation-triangle"></i></span>
          <div>
            <h5 class="alert-heading">Achtung!</h5>
            <p class="mb-0">Diese Aktion kann nicht rückgängig gemacht werden.</p>
          </div>
        </div>
        
        <p>
          Sind Sie sicher, dass Sie die Firma <strong>{{ companyToDelete ? companyToDelete.name : '' }}</strong> löschen möchten?
        </p>
        <p class="mb-0">
          Alle mit dieser Firma verbundenen Daten, Tests und Einträge werden ebenfalls gelöscht.
        </p>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useCompanyStore } from '@/stores/company';
import { useToast } from 'vue-toastification';
import Card from '@/components/ui/Card.vue';
import * as bootstrap from 'bootstrap';

export default defineComponent({
  name: 'CompanyList',
  components: {
    Card
  },
  setup() {
    const companyStore = useCompanyStore();
    const toast = useToast();
    
    // Refs (state)
    const loading = ref(true);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const companyForm = ref({
      name: '',
      email: '',
      phone: '',
      website: '',
      logo: '',
      description: '',
      active: true
    });
    const formErrors = ref({});
    const creatingCompany = ref(false);
    const updatingCompany = ref(false);
    const deletingCompany = ref(false);
    const companyToDelete = ref(null);
    const editingCompanyId = ref(null);
    
    // Computed properties
    const companies = computed(() => {
      return companyStore.companies;
    });
    
    const filteredCompanies = computed(() => {
      if (!searchQuery.value) {
        return companies.value;
      }
      
      const query = searchQuery.value.toLowerCase();
      return companies.value.filter(company => {
        return (
          company.name.toLowerCase().includes(query) ||
          (company.email && company.email.toLowerCase().includes(query)) ||
          (company.phone && company.phone.toLowerCase().includes(query)) ||
          (company.description && company.description.toLowerCase().includes(query))
        );
      });
    });
    
    const paginatedCompanies = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage.value;
      const endIndex = startIndex + itemsPerPage.value;
      return filteredCompanies.value.slice(startIndex, endIndex);
    });
    
    const isFormValid = computed(() => {
      return !!companyForm.value.name.trim();
    });
    
    // Methods
    const loadCompanies = async () => {
      loading.value = true;
      try {
        await companyStore.fetchCompanies();
      } catch (error) {
        toast.error('Fehler beim Laden der Firmen');
        console.error('Fehler beim Laden der Firmen:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const resetForm = () => {
      companyForm.value = {
        name: '',
        email: '',
        phone: '',
        website: '',
        logo: '',
        description: '',
        active: true
      };
      formErrors.value = {};
      editingCompanyId.value = null;
    };
    
    const showCreateForm = () => {
      resetForm();
      // Für Bootstrap 5 müssen wir Modal manuell instanziieren
      const modalElement = document.getElementById('modal-create-company');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    };
    
    const editCompany = (company) => {
      resetForm();
      editingCompanyId.value = company.id;
      
      // Formular mit Daten der ausgewählten Firma befüllen
      companyForm.value = {
        name: company.name,
        email: company.email || '',
        phone: company.phone || '',
        website: company.website || '',
        logo: company.logo || '',
        description: company.description || '',
        active: company.active
      };
      
      // Für Bootstrap 5 müssen wir Modal manuell instanziieren
      const modalElement = document.getElementById('modal-edit-company');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    };
    
    const confirmDeleteCompany = (company) => {
      companyToDelete.value = company;
      // Für Bootstrap 5 müssen wir Modal manuell instanziieren
      const modalElement = document.getElementById('modal-delete-company');
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    };
    
    const validateForm = () => {
      const errors = {};
      
      if (!companyForm.value.name.trim()) {
        errors.name = 'Der Firmenname ist erforderlich';
      }
      
      if (companyForm.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyForm.value.email)) {
        errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
      }
      
      if (companyForm.value.website && 
          !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}$/i.test(companyForm.value.website)) {
        errors.website = 'Bitte geben Sie eine gültige Website-URL ein';
      }
      
      formErrors.value = errors;
      return Object.keys(errors).length === 0;
    };
    
    const createCompany = async () => {
      if (!validateForm()) return;
      
      creatingCompany.value = true;
      
      try {
        await companyStore.createCompany(companyForm.value);
        toast.success('Firma wurde erfolgreich erstellt');
        resetForm();
        // Schließe das Modal
      } catch (error) {
        handleApiErrors(error);
        toast.error('Fehler beim Erstellen der Firma');
      } finally {
        creatingCompany.value = false;
      }
    };
    
    const updateCompany = async () => {
      if (!validateForm() || !editingCompanyId.value) return;
      
      updatingCompany.value = true;
      
      try {
        await companyStore.updateCompany({
          id: editingCompanyId.value,
          ...companyForm.value
        });
        toast.success('Firma wurde erfolgreich aktualisiert');
        resetForm();
        // Schließe das Modal
      } catch (error) {
        handleApiErrors(error);
        toast.error('Fehler beim Aktualisieren der Firma');
      } finally {
        updatingCompany.value = false;
      }
    };
    
    const deleteCompany = async () => {
      if (!companyToDelete.value) return;
      
      deletingCompany.value = true;
      
      try {
        await companyStore.deleteCompany(companyToDelete.value.id);
        toast.success('Firma wurde erfolgreich gelöscht');
        companyToDelete.value = null;
        // Schließe das Modal
      } catch (error) {
        toast.error('Fehler beim Löschen der Firma');
      } finally {
        deletingCompany.value = false;
      }
    };
    
    const saveCompany = () => {
      if (editingCompanyId.value) {
        updateCompany();
      } else {
        createCompany();
      }
    };
    
    const handleApiErrors = (error) => {
      if (error.response?.data?.errors) {
        formErrors.value = error.response.data.errors;
      }
    };
    
    const getInitials = (name) => {
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    };
    
    const getColorForCompany = (id) => {
      const colors = [
        '#4361ee', '#3a0ca3', '#7209b7', '#f72585',
        '#4cc9f0', '#4895ef', '#560bad', '#f15bb5',
        '#fee440', '#00bbf9', '#00f5d4', '#9b5de5'
      ];
      
      // Einfache Hash-Funktion, um eine Farbe für eine bestimmte ID auszuwählen
      const colorIndex = parseInt(id) % colors.length;
      return colors[colorIndex];
    };
    
    // Lifecycle hooks
    onMounted(() => {
      loadCompanies();
    });
    
    return {
      // state
      loading,
      searchQuery,
      currentPage,
      itemsPerPage,
      companyForm,
      formErrors,
      creatingCompany,
      updatingCompany,
      deletingCompany,
      companyToDelete,
      editingCompanyId,
      
      // computed
      companies,
      filteredCompanies,
      paginatedCompanies,
      isFormValid,
      
      // methods
      loadCompanies,
      resetForm,
      showCreateForm,
      editCompany,
      confirmDeleteCompany,
      validateForm,
      createCompany,
      updateCompany,
      deleteCompany,
      saveCompany,
      handleApiErrors,
      getInitials,
      getColorForCompany
    };
  }
});
</script> 